/**
 * Cooperative scheduling helpers used by long-running main-thread work
 * (e.g. sampling dense SVG paths) so the browser can paint and handle input
 * between compute slices.
 *
 * Strategy:
 * - Hot path uses time-budget checks: `yieldIfFrameExhausted` is cheap when
 *   the current frame still has time, and only yields the event loop when
 *   the budget runs out. This avoids the ~4ms `setTimeout(0)` clamp cost
 *   when many yields would fire per frame.
 * - Yields go through a `MessageChannel` port to get back on the task queue
 *   nearly immediately (~0ms) so the caller can keep working after one
 *   paint/input cycle.
 */

const DEFAULT_FRAME_BUDGET_MS = 8;

let messageChannelPort: MessagePort | null = null;
let pendingYieldResolvers: Array<() => void> = [];

function ensureChannel(): MessagePort | null {
    if (typeof MessageChannel === "undefined") return null;
    if (messageChannelPort) return messageChannelPort;
    const channel = new MessageChannel();
    channel.port1.onmessage = () => {
        const queue = pendingYieldResolvers;
        pendingYieldResolvers = [];
        for (const resolve of queue) resolve();
    };
    messageChannelPort = channel.port2;
    return messageChannelPort;
}

/** Yield the event loop once (≈ postTask with user-blocking priority). */
export function yieldToMain(): Promise<void> {
    const port = ensureChannel();
    if (!port) {
        return new Promise((resolve) => setTimeout(resolve, 0));
    }
    return new Promise((resolve) => {
        pendingYieldResolvers.push(resolve);
        port.postMessage(null);
    });
}

export interface FrameBudget {
    /** Wall-clock start of the current slice. */
    sliceStart: number;
    /** Max ms to spend on this slice before yielding. */
    budgetMs: number;
}

export function startFrameBudget(budgetMs: number = DEFAULT_FRAME_BUDGET_MS): FrameBudget {
    return {
        sliceStart: performance.now(),
        budgetMs,
    };
}

function isAborted(signal: AbortSignal | undefined): boolean {
    return !!signal && signal.aborted;
}

function throwIfAborted(signal: AbortSignal | undefined): void {
    if (isAborted(signal)) {
        throw new DOMException("Aborted", "AbortError");
    }
}

/**
 * Yield to the event loop **only** if the current slice has exceeded its
 * budget. Cheap to call per-iteration; resets the budget after yielding.
 */
export async function yieldIfFrameExhausted(
    budget: FrameBudget,
    signal?: AbortSignal,
): Promise<void> {
    throwIfAborted(signal);
    if (performance.now() - budget.sliceStart < budget.budgetMs) return;
    await yieldToMain();
    throwIfAborted(signal);
    budget.sliceStart = performance.now();
}
